import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import "./ZYPersonalities.css";

/*
 * La información visual permanece en el componente.
 *
 * Los nombres, descripciones y mensajes se obtienen
 * desde los archivos home.json.
 */
const PERSONALITY_CONFIG = [
  {
    id: "mama-latina",
    accent: "#e94788",
    accentSoft: "#d94f86",
    screenGlow: "rgba(233, 71, 136, 0.2)",
  },
  {
    id: "godin-pro",
    accent: "#4166d9",
    accentSoft: "#5579db",
    screenGlow: "rgba(65, 102, 217, 0.2)",
  },
  {
    id: "tia-mistica",
    accent: "#8750d4",
    accentSoft: "#965bd8",
    screenGlow: "rgba(135, 80, 212, 0.21)",
  },
  {
    id: "sensei-otaku",
    accent: "#dc782d",
    accentSoft: "#e8873f",
    screenGlow: "rgba(220, 120, 45, 0.21)",
  },
  {
    id: "compa-barrio",
    accent: "#2d9f50",
    accentSoft: "#3eaa5d",
    screenGlow: "rgba(45, 159, 80, 0.21)",
  },
];

function PhoneStatusBar() {
  return (
    <>
      <img
        className="zy-personality-phone__status-image"
        src="/status-bar-iphone.png"
        alt=""
        aria-hidden="true"
      />

      <span
        className="zy-personality-phone__dynamic-island"
        aria-hidden="true"
      />
    </>
  );
}

function PhoneSideButtons() {
  return (
    <>
      <div
        className="zy-personality-phone__buttons zy-personality-phone__buttons--left"
        aria-hidden="true"
      >
        <i />
        <i />
        <i />
      </div>

      <div
        className="zy-personality-phone__buttons zy-personality-phone__buttons--right"
        aria-hidden="true"
      >
        <i />
      </div>
    </>
  );
}

function PersonalityPhone({ personality, index, phoneTranslations }) {
  return (
    <article
      className="zy-personality"
      style={{
        "--personality-accent": personality.accent,
        "--personality-accent-soft": personality.accentSoft,
        "--personality-screen-glow": personality.screenGlow,
        "--personality-delay": `${index * 120}ms`,
      }}
    >
      <header className="zy-personality__heading">
        <h3>{personality.name}</h3>
        <p>{personality.tagline}</p>
      </header>

      <div className="zy-personality-phone">
        <div className="zy-personality-phone__frame">
          <PhoneSideButtons />

          <div className="zy-personality-phone__screen">
            <PhoneStatusBar />

            <header className="zy-personality-phone__header">
              <button
                type="button"
                className="zy-personality-phone__back"
                aria-label={phoneTranslations.backLabel}
                tabIndex={-1}
              >
                <FiChevronLeft />
              </button>

              <div className="zy-personality-phone__avatar">ZY</div>

              <div className="zy-personality-phone__identity">
                <strong>{personality.name}</strong>

                <span>
                  <i />
                  {phoneTranslations.online}
                </span>
              </div>
            </header>

            <main className="zy-personality-phone__chat">
              {personality.messages.map((message, messageIndex) => (
                <div
                  key={message.id}
                  className={[
                    "zy-personality-message",
                    message.sender === "user"
                      ? "zy-personality-message--user"
                      : "zy-personality-message--assistant",
                  ].join(" ")}
                  style={{
                    "--message-delay": `${index * 120 + messageIndex * 110}ms`,
                  }}
                >
                  {message.content}
                </div>
              ))}
            </main>
            <img
              className="zy-personality-phone__bottom-bar-image"
              src="/bottom-bar.png"
              alt=""
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    </article>
  );
}

export default function ZYPersonalities() {
  const { t } = useTranslation("home");

  const sectionRef = useRef(null);
  const carouselRef = useRef(null);

  const [isVisible, setIsVisible] = useState(false);
  const [activePersonality, setActivePersonality] = useState(0);

  const personalities = useMemo(() => {
    const translatedPersonalities = t("zyPersonalities.personalities", {
      returnObjects: true,
    });

    if (!Array.isArray(translatedPersonalities)) {
      return [];
    }

    return PERSONALITY_CONFIG.map((configItem) => {
      const translatedPersonality = translatedPersonalities.find(
        (personality) => personality.id === configItem.id,
      );

      return {
        ...configItem,
        name: translatedPersonality?.name ?? configItem.id,
        tagline: translatedPersonality?.tagline ?? "",
        messages: Array.isArray(translatedPersonality?.messages)
          ? translatedPersonality.messages
          : [],
      };
    });
  }, [t]);

  const phoneTranslations = useMemo(() => {
    const result = t("zyPersonalities.phone", {
      returnObjects: true,
    });

    if (!result || typeof result !== "object" || Array.isArray(result)) {
      return {
        online: "",
        backLabel: "",
      };
    }

    return result;
  }, [t]);

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        setIsVisible(true);
        observer.disconnect();
      },
      {
        threshold: 0.2,
      },
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  const updateActivePersonality = useCallback(() => {
    const carousel = carouselRef.current;

    if (!carousel) {
      return;
    }

    const slides = Array.from(carousel.querySelectorAll(".zy-personality"));

    if (!slides.length) {
      return;
    }

    const carouselRect = carousel.getBoundingClientRect();
    const carouselCenter = carouselRect.left + carouselRect.width / 2;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const slideRect = slide.getBoundingClientRect();
      const slideCenter = slideRect.left + slideRect.width / 2;
      const distance = Math.abs(carouselCenter - slideCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActivePersonality(closestIndex);
  }, []);

  useEffect(() => {
    const carousel = carouselRef.current;

    if (!carousel) {
      return undefined;
    }

    let animationFrame = null;

    const handleScroll = () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }

      animationFrame = window.requestAnimationFrame(updateActivePersonality);
    };

    carousel.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    window.addEventListener("resize", updateActivePersonality);

    updateActivePersonality();

    return () => {
      carousel.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateActivePersonality);

      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [updateActivePersonality]);

  const goToPersonality = useCallback((index) => {
    const carousel = carouselRef.current;

    if (!carousel) {
      return;
    }

    const slides = carousel.querySelectorAll(".zy-personality");
    const targetSlide = slides[index];

    if (!targetSlide) {
      return;
    }

    targetSlide.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });

    setActivePersonality(index);
  }, []);

  const goToPrevious = () => {
    const previousIndex =
      activePersonality === 0
        ? personalities.length - 1
        : activePersonality - 1;

    goToPersonality(previousIndex);
  };

  const goToNext = () => {
    const nextIndex =
      activePersonality === personalities.length - 1
        ? 0
        : activePersonality + 1;

    goToPersonality(nextIndex);
  };

  return (
    <section
      ref={sectionRef}
      className={["zy-personalities", isVisible ? "is-visible" : ""]
        .filter(Boolean)
        .join(" ")}
      aria-labelledby="zy-personalities-title"
      aria-label={t("zyPersonalities.accessibility.sectionLabel")}
    >
      <div className="zy-personalities__glow" aria-hidden="true" />

      <header className="zy-personalities__header">
        <h2 id="zy-personalities-title">
          {t("zyPersonalities.header.titleBefore")}{" "}
          <span>{t("zyPersonalities.header.titleHighlight")}</span>
          <br />
          {t("zyPersonalities.header.titleAfter")}
        </h2>

        <p>{t("zyPersonalities.header.description")}</p>
      </header>

      <div
        ref={carouselRef}
        className="zy-personalities__grid"
        aria-label={t("zyPersonalities.accessibility.carouselLabel")}
      >
        {personalities.map((personality, index) => (
          <PersonalityPhone
            key={personality.id}
            personality={personality}
            index={index}
            phoneTranslations={phoneTranslations}
          />
        ))}
      </div>

      <nav
        className="zy-personalities__carousel-controls"
        aria-label={t("zyPersonalities.accessibility.controlsLabel")}
      >
        <button
          type="button"
          className="zy-personalities__arrow"
          aria-label={t("zyPersonalities.accessibility.previousLabel")}
          onClick={goToPrevious}
        >
          <FiChevronLeft />
        </button>

        <div className="zy-personalities__dots">
          {personalities.map((personality, index) => (
            <button
              key={personality.id}
              type="button"
              className={[
                "zy-personalities__dot",
                activePersonality === index ? "is-active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-label={t(
                "zyPersonalities.accessibility.showPersonalityLabel",
                {
                  name: personality.name,
                },
              )}
              aria-current={activePersonality === index ? "true" : undefined}
              onClick={() => goToPersonality(index)}
            />
          ))}
        </div>

        <button
          type="button"
          className="zy-personalities__arrow"
          aria-label={t("zyPersonalities.accessibility.nextLabel")}
          onClick={goToNext}
        >
          <FiChevronRight />
        </button>
      </nav>
    </section>
  );
}
